import { useSearchParams } from 'react-router-dom';
import { FilterBar } from '@/components/forms/FilterBar';
import { FormField } from '@/components/forms/FormField';
import { ActionButton } from '@/components/ui/ActionButton';
import { BucketBadge } from '@/components/ui/StatusBadge';
import type { Bucket } from '@/lib/types';

const HISTORY_BUCKETS: readonly Bucket[] = [
  'POSTED',
  'POSTED_SERVICE',
  'DUPLICATE',
  'REJECTED',
] as const;

/**
 * History tab filter strip. Reads/writes URL search params:
 *
 *   ?vendor=&po_no=&grn_no=&invoice_no=&bucket=&processed_from=&processed_to=&limit=&offset=
 *
 * The legacy `?status=` chip strip was removed because for History rows it
 * was redundant with `?bucket=` — every row has status ∈ {posted, rejected,
 * cancelled} and bucket gives a strict superset (POSTED, POSTED_SERVICE,
 * DUPLICATE, REJECTED). Backend still accepts ?status= for backward compat.
 *
 * `processed_from` / `processed_to` are ISO `YYYY-MM-DD` strings.
 * `limit` defaults to 50, `offset` to 0 — managed by the data hook, not here.
 */
export function HistoryFilters() {
  const [params, setParams] = useSearchParams();

  const vendor = params.get('vendor') ?? '';
  const poNo = params.get('po_no') ?? '';
  const grnNo = params.get('grn_no') ?? '';
  const invoiceNo = params.get('invoice_no') ?? '';
  const processedFrom = params.get('processed_from') ?? '';
  const processedTo = params.get('processed_to') ?? '';

  const selectedBucket = (params.get('bucket') ?? '') as Bucket | '';

  const update = (key: string, value: string) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('offset');
        return next;
      },
      { replace: true },
    );
  };

  const toggleBucket = (bucket: Bucket) => {
    update('bucket', selectedBucket === bucket ? '' : bucket);
  };

  const clear = () => {
    setParams(
      (prev) => {
        const next = new URLSearchParams();
        const tab = prev.get('tab');
        if (tab) next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <FilterBar
      actions={
        <ActionButton variant="ghost" size="sm" onClick={clear} type="button">
          Clear filters
        </ActionButton>
      }
    >
      <FormField
        kind="text"
        label="Vendor"
        placeholder="Contains…"
        value={vendor}
        onChange={(v) => update('vendor', v)}
        className="min-w-[160px]"
      />
      <FormField
        kind="text"
        label="PO #"
        placeholder="Contains…"
        value={poNo}
        onChange={(v) => update('po_no', v)}
        className="min-w-[140px]"
      />
      <FormField
        kind="text"
        label="GRN #"
        placeholder="Contains…"
        value={grnNo}
        onChange={(v) => update('grn_no', v)}
        className="min-w-[140px]"
      />
      <FormField
        kind="text"
        label="Invoice #"
        placeholder="Contains…"
        value={invoiceNo}
        onChange={(v) => update('invoice_no', v)}
        className="min-w-[140px]"
      />
      <FormField
        kind="date"
        label="Processed from"
        placeholder=""
        value={processedFrom}
        onChange={(v) => update('processed_from', v)}
        className="min-w-[160px]"
      />
      <FormField
        kind="date"
        label="Processed to"
        placeholder=""
        value={processedTo}
        onChange={(v) => update('processed_to', v)}
        className="min-w-[160px]"
      />
      <fieldset className="flex flex-col gap-6">
        <legend className="block text-sm font-medium text-neutral-700">Bucket</legend>
        <div className="flex flex-wrap items-center gap-8">
          {HISTORY_BUCKETS.map((b) => {
            const active = selectedBucket === b;
            return (
              <button
                key={b}
                type="button"
                onClick={() => toggleBucket(b)}
                aria-pressed={active}
                className={
                  'rounded-full border p-2 transition-colors ' +
                  (active
                    ? 'border-primary-500 ring-2 ring-primary-500/30'
                    : 'border-transparent hover:border-neutral-300')
                }
              >
                <BucketBadge bucket={b} />
              </button>
            );
          })}
        </div>
      </fieldset>
    </FilterBar>
  );
}
